import { getCurrDatetime } from '../utils';

// Accepts any document shape: docs that cannot carry an archived field are never archived.
export const isArchived = (doc: object): boolean => (doc as { archived?: boolean }).archived === true;

export const isDeleted = (doc: { _deleted?: boolean }): boolean => doc._deleted === true;

// Mango clause matching only non-archived docs. "Not archived" has two encodings:
// archived absent (local docs predating the rollout/backfill) or explicitly false
// (created or restored docs). The RxDB matcher is unreliable for absent fields, so
// both arms are spelled out.
export const notArchivedSelector = {
  $or: [{ archived: { $eq: false } }, { archived: { $exists: false } }],
};

// Single write site for archive state. `archived` is the replication-partition
// discriminant; `archivedAt` records when the document was last archived and survives a
// restore, so a stale archived pull can be ordered against the local archive state.
export const buildArchivePatch = (archived: boolean): { archived: boolean; archivedAt?: string } =>
  archived ? { archived: true, archivedAt: getCurrDatetime() } : { archived: false };

// A collection is archive-partitioned when its schema carries the `archived` discriminant
// (notes/blocks/userSources/instructions); tags and other collections do not.
export const isArchivePartitioned = (rxCollection: {
  schema: { jsonSchema: { properties: Record<string, unknown> } };
}): boolean => 'archived' in rxCollection.schema.jsonSchema.properties;

// Rolling window after which a tombstone is hard-deleted from Firestore by the
// daily purge sweep. The window is long enough that a device offline for weeks still
// pulls the tombstone and learns about the deletion, instead of pushing the document
// back as live.
export const PURGE_AFTER_DAYS = 60;

const SHELL_KEPT_FIELDS = new Set([
  'id',
  'userId',
  'archived',
  'archivedAt',
  'createdAt',
  'updatedAt',
  // GCS handle: purgeDeletedUserSourceFile, its shared-source guard, and
  // orphaned-file tooling all read these off the tombstone
  'source',
  'sourceType',
]);

type TombstoneSchemaProperty = {
  type?: string | readonly string[];
  enum?: readonly unknown[];
};

export const buildTombstoneShell = (
  doc: Record<string, unknown> & { _deleted?: boolean },
  jsonSchema: { properties: Record<string, TombstoneSchemaProperty> }
): Record<string, unknown> => {
  const shell: Record<string, unknown> = {};
  for (const [key, property] of Object.entries(jsonSchema.properties)) {
    const value = SHELL_KEPT_FIELDS.has(key) || property.enum ? doc[key] : emptyValueForType(property);
    // A kept field absent from the doc stays absent: Firestore writes reject undefined
    // values, and the shell is a full overwrite of a doc that lacks the field too.
    if (value !== undefined) {
      shell[key] = value;
    }
  }
  shell._deleted = true;
  return shell;
};

const emptyValueForType = (property: TombstoneSchemaProperty): unknown => {
  if (Array.isArray(property.type) && property.type.includes('null')) {
    return null;
  }

  const primaryType = Array.isArray(property.type) ? property.type[0] : property.type;
  switch (primaryType) {
    case 'string':
      return '';
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return null;
  }
};
